import { FC, useMemo, useState } from "react";
import {
  BoostingCampaign,
  LinkedBoostingCampaign,
} from "@vviinn/user-managment-client-ts";
import { BoostingCampaignScoreContributionStrategyEnum } from "@vviinn/user-managment-client-ts/dist/models/BoostingCampaign";
import { SlideOverWrapper } from "../../../ui/layout/organisms/SlideOver/SlideOverWrapper/SlideOverWrapper";
import { Button } from "../../../ui/layout/molecules/Button";
import { NamedTextInput } from "../../../ui/layout/molecules/NamedTextInput";
import { NamedSelect } from "../../../ui/layout/molecules/NamedSelect";
import { NamedNumberic } from "../../../ui/layout/molecules/NamedNumberic";
import { PreferredValuesList } from "./PreferredValuesList";
import { RestrictedSiteCustomField } from "../../../site";
import {
  STRATEGY_OPTIONS,
  EMPTY_PREFERRED_VALUE,
  PREFERRED_VALUE_REQUIRED_KEYS,
} from "../constants";
import {
  PreferredValueDraft,
  inferColumnType,
  serializePreferredValues,
  deserializePreferredValues,
  rawValueToNumber,
  patchFirstPreferredDraft,
} from "../utils";
import { WeightNumberInput } from "./WeightNumberInput";
import { SubSectionWithStyles } from "../../../ui/layout";
import { CollapsedFields } from "../../../components/GeneralUI/CollapsedFields/CollapsedFields";
import { detailsBlockStyles } from "../../../ui/layout/detailedPages/detailsBlockStyles";
import { getFieldsProps } from "../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/helpers/getFieldsProps";

export type LinkedBoostingSlideOverProps = {
  open: boolean;
  onClose: () => void;
  columnOptions: string[];
  customFields: RestrictedSiteCustomField[];
  initialLink?: LinkedBoostingCampaign;
  availableCampaigns?: BoostingCampaign[];
  onSubmit: (link: LinkedBoostingCampaign) => Promise<boolean>;
  onCreateCopy?: (link: LinkedBoostingCampaign) => Promise<boolean>;
};

const emptyLink = (): LinkedBoostingCampaign => ({
  weight: 0,
  campaign: {
    siteId: 0,
    name: "",
    scoreContributionStrategy: "PREFER_HIGHER_RANK",
  },
});

const { detailsSwitchableStyles, customWrapperStyles } =
  detailsBlockStyles(true);

export const LinkedBoostingSlideOver: FC<LinkedBoostingSlideOverProps> = ({
  open,
  onClose,
  columnOptions,
  customFields,
  initialLink,
  availableCampaigns,
  onSubmit,
  onCreateCopy,
}) => {
  const isNewDraftMode = availableCampaigns !== undefined;

  const getInitialLink = (): LinkedBoostingCampaign => {
    if (initialLink) return initialLink;
    if (availableCampaigns?.length)
      return { weight: 0, campaign: availableCampaigns[0] };
    const base = emptyLink();
    return {
      ...base,
      campaign: { ...base.campaign, column: columnOptions.at(0) },
    };
  };

  const defaultAddMode: "existing" | "new" =
    (availableCampaigns?.length ?? 0) > 0 ? "existing" : "new";

  const [link, setLink] = useState<LinkedBoostingCampaign>(getInitialLink);
  const [preferredDrafts, setPreferredDrafts] = useState<PreferredValueDraft[]>(
    () => deserializePreferredValues(getInitialLink().campaign.preferredValues),
  );
  const [addMode, setAddMode] = useState<"existing" | "new">(defaultAddMode);

  const { handleAdd, handleRemove, handleChange, areSameFields } = useMemo(
    () =>
      getFieldsProps<PreferredValueDraft>(
        setPreferredDrafts,
        EMPTY_PREFERRED_VALUE,
        PREFERRED_VALUE_REQUIRED_KEYS,
      ),
    [],
  );

  const colType = inferColumnType(link.campaign.column, customFields);
  const handleWeightChange = (v: number) =>
    setLink((prev) => ({ ...prev, weight: v }));
  const isValid = !!link.campaign.name && !!link.campaign.column;

  const allowSubmit = useMemo(() => {
    if (isNewDraftMode) return true;
    const isSameLinkFields =
      link.weight === (initialLink?.weight ?? 0) &&
      link.campaign.name === (initialLink?.campaign.name ?? "") &&
      link.campaign.column === initialLink?.campaign.column &&
      link.campaign.scoreContributionStrategy ===
        (initialLink?.campaign.scoreContributionStrategy ??
          "PREFER_HIGHER_RANK");
    return (
      !isSameLinkFields ||
      !areSameFields(
        deserializePreferredValues(initialLink?.campaign.preferredValues),
        preferredDrafts,
      )
    );
  }, [link, preferredDrafts, initialLink, isNewDraftMode, areSameFields]);

  const setCampaign = (patch: Partial<BoostingCampaign>) =>
    setLink((prev) => ({ ...prev, campaign: { ...prev.campaign, ...patch } }));

  const buildLink = (): LinkedBoostingCampaign => {
    if (isNewDraftMode && addMode === "existing") return link;
    const preferredValues = serializePreferredValues(preferredDrafts);
    return {
      ...link,
      campaign: {
        ...link.campaign,
        preferredValues: preferredValues.length ? preferredValues : undefined,
      },
    };
  };

  const handleSave = async (): Promise<boolean> => {
    if (!isValid) return false;
    const success = await onSubmit(buildLink());
    if (success && isNewDraftMode) onClose();
    return success;
  };

  return (
    <SlideOverWrapper
      open={open}
      onClose={onClose}
      title={
        isNewDraftMode ? "Add Boosting Campaign" : "Edit Boosting Campaign"
      }
      buttonProps={{
        onClick: handleSave,
        disabled: !isValid || (!isNewDraftMode && !allowSubmit),
        title: isNewDraftMode ? undefined : "Update for all campaigns",
      }}
      initConfig={() => {
        setAddMode(defaultAddMode);
        const initial = getInitialLink();
        setLink(initial);
        setPreferredDrafts(
          deserializePreferredValues(initial.campaign.preferredValues),
        );
      }}
    >
      {isNewDraftMode && (
        <SubSectionWithStyles title="Add mode">
          <NamedSelect
            title="Mode"
            options={[
              { title: "Link existing", value: "existing" },
              { title: "Create new", value: "new" },
            ]}
            value={addMode}
            onChange={({ value }) => {
              const mode = value as "existing" | "new";
              setAddMode(mode);
              if (mode === "existing" && availableCampaigns?.length) {
                setLink({ weight: 0, campaign: availableCampaigns[0] });
                setPreferredDrafts(
                  deserializePreferredValues(
                    availableCampaigns[0].preferredValues,
                  ),
                );
              } else {
                const base = emptyLink();
                setLink({
                  ...base,
                  campaign: { ...base.campaign, column: columnOptions.at(0) },
                });
                setPreferredDrafts([]);
              }
            }}
          />
        </SubSectionWithStyles>
      )}

      {isNewDraftMode && addMode === "existing" ? (
        <SubSectionWithStyles title="Campaign">
          <NamedSelect
            title="Campaign"
            options={(availableCampaigns ?? []).map(({ id, name }) => ({
              title: name,
              value: id ?? "",
            }))}
            value={link.campaign.id ?? ""}
            onChange={({ value }) => {
              const found = (availableCampaigns ?? []).find(
                (c) => c.id === value,
              );
              if (found) {
                setLink((prev) => ({ ...prev, campaign: found }));
                setPreferredDrafts(
                  deserializePreferredValues(found.preferredValues),
                );
              }
            }}
          />
          <WeightNumberInput
            value={link.weight}
            onChange={handleWeightChange}
          />
        </SubSectionWithStyles>
      ) : (
        <>
          <CollapsedFields
            title="Campaign settings"
            isSwitchable={false}
            active={true}
            customGroupStyles={detailsSwitchableStyles}
            customWrapperStyles={customWrapperStyles}
          >
            <NamedTextInput
              title="Name"
              value={link.campaign.name ?? ""}
              onChange={({ value }) => setCampaign({ name: value })}
            />
            <NamedSelect
              title="Column"
              options={columnOptions}
              value={link.campaign.column ?? ""}
              onChange={({ value }) => {
                setCampaign({ column: value, preferredValues: undefined });
                setPreferredDrafts([]);
              }}
            />
            <NamedSelect
              title="Boosting strategy"
              options={STRATEGY_OPTIONS}
              value={
                link.campaign.scoreContributionStrategy ?? "PREFER_HIGHER_RANK"
              }
              onChange={({ value }) =>
                setCampaign({
                  scoreContributionStrategy:
                    value as BoostingCampaignScoreContributionStrategyEnum,
                })
              }
            />
            <WeightNumberInput
              value={link.weight}
              onChange={handleWeightChange}
            />
          </CollapsedFields>

          {link.campaign.column && colType === "number" && (
            <CollapsedFields
              title="Preferred value"
              active={true}
              isSwitchable={false}
              customGroupStyles={detailsSwitchableStyles}
              customWrapperStyles={customWrapperStyles}
            >
              <WeightNumberInput
                value={preferredDrafts[0]?.weight ?? 1}
                onChange={(v) =>
                  setPreferredDrafts(
                    patchFirstPreferredDraft(preferredDrafts[0], { weight: v }),
                  )
                }
              />
              <NamedNumberic
                title="Value"
                value={rawValueToNumber(preferredDrafts[0]?.rawValue)}
                step="any"
                onChange={(target) =>
                  setPreferredDrafts(
                    patchFirstPreferredDraft(preferredDrafts[0], {
                      rawValue: target.value,
                    }),
                  )
                }
              />
            </CollapsedFields>
          )}

          {link.campaign.column && colType !== "number" && (
            <PreferredValuesList
              drafts={preferredDrafts}
              colType={colType}
              onAdd={handleAdd}
              onRemove={handleRemove}
              onUpdate={handleChange}
            />
          )}
        </>
      )}

      {!isNewDraftMode && onCreateCopy && (
        <Button
          title="Create New Boosting"
          buttonType="primary"
          disabled={!isValid || !allowSubmit}
          onClick={async () => {
            const success = await onCreateCopy(buildLink());
            if (success) onClose();
          }}
          className="w-full"
        />
      )}
    </SlideOverWrapper>
  );
};
